// Het ontwerp uit de webshop-editor in het dashboard (/Webshopeditor, akkoord Martijn 13-10-2026).
//
// De api (GET api/Webshop/Ontwerp) geeft de gepubliceerde stand van deze webshopwidget, of in de editor (bewerkteken + concept=1)
// het concept. Zonder ontwerp (null) ziet de webshop er precies uit zoals altijd: elke functie hier geeft dan terug wat erin ging.
//
// Het ontwerp gaat over wat per webshop verschilt: de voorpaginategels, de welkomsttekst, het menu, welke categorieen en producten
// deze webshop verbergt, uitgelicht en volgorde, de kleuren (huisstijl of eigen) en de hoekafronding, en de webshop-opties.
// Naam, prijs, foto van een product en de naam van een categorie staan niet in het ontwerp: die gelden voor de kassa en elke
// webshop en komen gewoon uit de producten.
//
// Alleen pure functies (geen React), zodat de proeven ze los kunnen nakijken.

import { voegCssSamen } from "./weergave";

export const KLEURVELDEN = Object.freeze([
    "deliveryTypeScreenBgClr", "deliveryHeadingClr", "deliveryInfoClr", "deliveryCardBgClr", "deliveryCardImgBgClr",
    "deliveryCardTitleClr", "deliveryCardHoverClr", "ButtonBgClr", "ButtonTxtClr", "ButtonHoverClr",
]);

export const WELKOM_TITEL = "welkom.titel";
export const WELKOM_TEKST = "welkom.tekst";
export const KOPVELD = "PickupOrDeliveryScreenHeading";
export const INFOVELD = "PickupOrDeliveryScreenInfo";

const isHex = (w) => typeof w === "string" && /^#[0-9a-f]{6}$/i.test(w.trim());
const ids = (lijst) => (Array.isArray(lijst) ? lijst.map(Number).filter((n) => Number.isInteger(n) && n > 0) : []);
const kies = (waarde, lijst, standaard) => (lijst.includes(waarde) ? waarde : standaard);

/** Een schoon ontwerp, of null als er niets (bruikbaars) is. */
export function normaliseerOntwerp(ruw) {
    if (!ruw || typeof ruw !== "object" || Array.isArray(ruw)) return null;
    const vp = ruw.voorpagina && typeof ruw.voorpagina === "object" ? ruw.voorpagina : {};
    const menu = ruw.menu && typeof ruw.menu === "object" ? ruw.menu : {};
    const stijl = ruw.stijl && typeof ruw.stijl === "object" ? ruw.stijl : {};
    const producten = ruw.producten && typeof ruw.producten === "object" ? ruw.producten : {};
    const kleuren = {};
    for (const veld of KLEURVELDEN) {
        const k = stijl.kleuren?.[veld];
        if (isHex(k)) kleuren[veld] = k.trim().toUpperCase();
    }
    const volgorde = {};
    if (producten.volgorde && typeof producten.volgorde === "object") {
        for (const [cat, lijst] of Object.entries(producten.volgorde)) {
            const l = ids(lijst);
            if (/^\d+$/.test(cat) && l.length) volgorde[cat] = l;
        }
    }
    const hoek = Number.parseInt(stijl.hoek, 10);
    return {
        voorpagina: {
            vorm: kies(vp.vorm, ["kaart", "breed", "rond", "balk"], "kaart"),
            indeling: kies(vp.indeling, ["naast", "onder"], "naast"),
            tegels: (Array.isArray(vp.tegels) ? vp.tegels : [])
                .filter((t) => t && typeof t.methode === "string")
                .map((t, i) => ({
                    methode: t.methode,
                    beeld: kies(t.beeld, ["icoon", "foto", "geen"], "icoon"),
                    icoon: typeof t.icoon === "string" ? t.icoon : null,
                    kleur: isHex(t.kleur) ? t.kleur.toUpperCase() : null,
                    tekstkleur: isHex(t.tekstkleur) ? t.tekstkleur.toUpperCase() : null,
                    volgorde: Number.isFinite(Number(t.volgorde)) ? Number(t.volgorde) : i,
                })),
        },
        teksten: ruw.teksten && typeof ruw.teksten === "object" ? ruw.teksten : {},
        fotos: ruw.fotos && typeof ruw.fotos === "object" ? ruw.fotos : {},
        menu: {
            grootte: kies(menu.grootte, ["klein", "normaal", "groot"], "normaal"),
            stijl: kies(menu.stijl, ["tabs", "pillen", "onderstreept", "tegels"], "tegels"),
            sticky: menu.sticky !== false,
        },
        categorieen: { verborgen: ids(ruw.categorieen?.verborgen) },
        producten: { uitgelicht: ids(producten.uitgelicht), verborgen: ids(producten.verborgen), volgorde },
        stijl: { eigen: stijl.eigen === true, kleuren, hoek: Number.isFinite(hoek) ? Math.max(0, Math.min(24, hoek)) : null },
        weergave: ruw.weergave && typeof ruw.weergave === "object" ? ruw.weergave : null,
    };
}

/** Een tekst uit het ontwerp in deze taal (NL, EN ...), of null. */
export function ontwerpTekst(ontwerp, sleutel, taal) {
    const perTaal = ontwerp?.teksten?.[sleutel];
    if (!perTaal || typeof perTaal !== "object") return null;
    const t = perTaal[String(taal || "").toUpperCase()];
    return typeof t === "string" && t.trim() ? t : null;
}

/** De eigen kleuren als platte velden ({ deliveryCardBgClr: '#fff', ... }), of null bij de huisstijl. */
export function ontwerpKleuren(ontwerp) {
    if (!ontwerp?.stijl?.eigen) return null;
    const k = ontwerp.stijl.kleuren || {};
    return Object.keys(k).length ? k : null;
}

/** De opmaakgroepen met de eigen kleuren van het ontwerp erover; zonder eigen kleuren precies wat erin ging. */
export function metOntwerpCss(groepen, ontwerp) {
    const plat = ontwerpKleuren(ontwerp);
    if (!plat || !Array.isArray(groepen)) return groepen;
    const nieuw = voegCssSamen(groepen, plat);
    return zelfdeCss(groepen, nieuw) ? groepen : nieuw;
}

function zelfdeCss(a, b) {
    if (a === b) return true;
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((g, i) => g === b[i] || JSON.stringify(g?.fields) === JSON.stringify(b[i]?.fields));
}

/** De widgetinstellingen met de hoekafronding van het ontwerp (opmaak.hoekafronding); zonder eigen hoek wat erin ging. */
export function metOntwerpHoek(config, ontwerp) {
    const hoek = ontwerp?.stijl?.hoek;
    if (hoek === null || hoek === undefined || !config || typeof config !== "object") return config;
    const opmaak = config.opmaak && typeof config.opmaak === "object" ? config.opmaak : {};
    if (Number.parseInt(opmaak.hoekafronding, 10) === hoek) return config;
    return { ...config, opmaak: { ...opmaak, hoekafronding: hoek } };
}

/**
 * De webshopinstellingen met de welkomsttitel en -tekst van het ontwerp in de taal van de widget (taalcode + idlanguage), in de
 * velden die de voorpagina al leest (PickupOrDeliveryScreenHeading/Info). Zonder tekst in die taal: wat erin ging.
 */
export function metWelkom(instellingen, ontwerp, taal, idlanguage) {
    if (!instellingen || typeof instellingen !== "object" || !ontwerp) return instellingen;
    const titel = ontwerpTekst(ontwerp, WELKOM_TITEL, taal);
    const tekst = ontwerpTekst(ontwerp, WELKOM_TEKST, taal);
    if (!titel && !tekst) return instellingen;

    const id = Number(idlanguage);
    const rijen = Array.isArray(instellingen.webshopWidgetSetupLanguages) ? [...instellingen.webshopWidgetSetupLanguages] : [];
    let anders = false;
    for (const [veld, waarde] of [[KOPVELD, titel], [INFOVELD, tekst]]) {
        if (!waarde) continue;
        const i = rijen.findIndex((r) => r?.fieldname === veld && Number(r?.idlanguage) === id);
        if (i < 0) { rijen.push({ fieldname: veld, idlanguage: id, data: waarde }); anders = true; }
        else if (rijen[i].data !== waarde) { rijen[i] = { ...rijen[i], data: waarde }; anders = true; }
    }
    return anders ? { ...instellingen, webshopWidgetSetupLanguages: rijen } : instellingen;
}

/** De webshop-opties met de weergave uit het ontwerp erover (alleen wat het ontwerp heeft). */
export function metOntwerpOpties(opties, ontwerp) {
    const w = ontwerp?.weergave;
    if (!w || !opties) return opties;
    // Alleen de velden van de weergave: de bezorgmethodes, tijden en verzendkosten staan niet in het ontwerp en komen live van de server.
    const uit = { ...opties };
    for (const veld of WEERGAVEVELDEN) if (w[veld] !== undefined && w[veld] !== null) uit[veld] = w[veld];
    return uit;
}

/** De webshop-opties die het ontwerp (tab Weergave en Menu van de editor) mag zetten. */
export const WEERGAVEVELDEN = Object.freeze([
    "weergave", "kolommenComputer", "kolommenTelefoon", "fotovorm", "toonBeschrijving", "toonAllergenen", "toonVoorraad", "toonPopulair",
    "categorieWeergave", "toonZoekbalk", "sortering", "minimaalBestelbedrag", "opmerkingPerProduct", "opmerkingPerBestelling", "levertijd",
    "bezorgkosten", "gratisVanaf",
]);

// ─── de voorpaginakaarten (stap 1, sluit aan op Methodekaarten en lib/kaartteksten) ─────────────────────────────────────

/**
 * Het ontwerp in de vorm die lib/kaartteksten.kaarttekst() leest ({ kaarten: { methode: { titel, ondertitel } } }), voor de taal van
 * de widget. Het ontwerp bewaart de teksten per taal (teksten["tegel.<methode>.titel"][taal]); zonder tekst in die taal: niets, en
 * dan geldt de standaardtekst van de kaart.
 */
export function kaartOntwerp(ontwerp, taal) {
    if (!ontwerp?.teksten) return null;
    const kaarten = {};
    for (const sleutel of Object.keys(ontwerp.teksten)) {
        const m = /^tegel\.([a-zA-Z0-9-]+)\.(titel|ondertitel)$/.exec(sleutel);
        if (!m) continue;
        const t = ontwerpTekst(ontwerp, sleutel, taal);
        if (!t) continue;
        kaarten[m[1]] = { ...(kaarten[m[1]] || {}), [m[2]]: t };
    }
    return Object.keys(kaarten).length ? { kaarten } : null;
}

/** De methodes in de volgorde van de tegels in het ontwerp; zonder volgorde (of zonder ontwerp) zoals ze binnenkwamen. */
export function tegelVolgorde(methodes, ontwerp) {
    const tegels = ontwerp?.voorpagina?.tegels;
    if (!Array.isArray(methodes) || !Array.isArray(tegels) || tegels.length === 0) return methodes;
    const plek = (m) => {
        const t = tegels.find((x) => x.methode === m);
        return t ? t.volgorde : 100 + methodes.indexOf(m);
    };
    return [...methodes].sort((a, b) => plek(a) - plek(b));
}

/**
 * Hoe een kaart eruitziet volgens het ontwerp: vorm (van alle kaarten), beeld (icoon, foto of geen), welk icoon, de foto en de
 * kleuren. Null zonder ontwerp: de kaart zoals hij altijd was.
 */
export function tegelVan(ontwerp, methode, fotoAdres) {
    if (!ontwerp?.voorpagina) return null;
    const t = (ontwerp.voorpagina.tegels || []).find((x) => x.methode === methode) || {};
    const pad = ontwerp.fotos?.[`tegel.${methode}`];
    const foto = t.beeld === "foto" && pad ? (typeof fotoAdres === "function" ? fotoAdres(pad) : pad) : null;
    return {
        vorm: ontwerp.voorpagina.vorm || "kaart",
        beeld: t.beeld === "foto" && !foto ? "icoon" : (t.beeld || "icoon"),
        icoon: t.icoon || methode,
        foto,
        kleur: t.kleur || null,
        tekstkleur: t.tekstkleur || null,
    };
}

/** Naast of onder elkaar (alle kaarten samen); zonder ontwerp naast elkaar, zoals altijd. */
export function kaartIndeling(ontwerp) {
    return ontwerp?.voorpagina?.indeling === "onder" ? "onder" : "naast";
}

// ─── menu en categorieen (stap 2) ──────────────────────────────────────────────────────────────────────────────

const MENUMATEN = {
    klein: { balk: { xs: 96, sm: 110 }, tegel: { xs: 68, sm: 84 }, tegelLetter: { xs: "9.5px", sm: "10.5px" }, letter: "13px", zijbalk: "100px", zijbalkLetter: "10.5px" },
    normaal: { balk: { xs: 120, sm: 140 }, tegel: { xs: 80, sm: 100 }, tegelLetter: { xs: "10px", sm: "11px" }, letter: "15px", zijbalk: "120px", zijbalkLetter: "11.5px" },
    groot: { balk: { xs: 140, sm: 170 }, tegel: { xs: 100, sm: 124 }, tegelLetter: { xs: "11px", sm: "13px" }, letter: "17px", zijbalk: "150px", zijbalkLetter: "13px" },
};

/**
 * Hoe het menu eruitziet: stijl (tabs, pillen, onderstreept, tegels), grootte en of het bovenaan blijft staan. Zonder ontwerp de
 * tegels met foto op de normale maat, sticky: precies zoals het menu altijd was.
 */
export function menuOpmaak(ontwerp) {
    const m = ontwerp?.menu || {};
    const grootte = MENUMATEN[m.grootte] ? m.grootte : "normaal";
    const maat = MENUMATEN[grootte];
    const stijl = ["tabs", "pillen", "onderstreept", "tegels"].includes(m.stijl) ? m.stijl : "tegels";
    return {
        stijl,
        grootte,
        sticky: m.sticky !== false,
        balkHoogte: stijl === "tegels" ? maat.balk : "auto",
        tegelBreedte: maat.tegel,
        tegelLetter: maat.tegelLetter,
        letter: maat.letter,
        zijbalkBreedte: maat.zijbalk,
        zijbalkLetter: maat.zijbalkLetter,
    };
}

/** De categorieen zonder die deze webshop verbergt; zonder ontwerp of niets verborgen precies wat erin ging. */
export function metOntwerpCategorieen(lijst, ontwerp) {
    const weg = ontwerp?.categorieen?.verborgen;
    if (!Array.isArray(lijst) || !Array.isArray(weg) || weg.length === 0) return lijst;
    const uit = lijst.filter((c) => !weg.includes(Number(c?.idCategory)));
    return uit.length === lijst.length ? lijst : uit;
}

// ─── producten (stap 3) ─────────────────────────────────────────────────────────────────────────────────────────

/**
 * De producten van een categorie zoals deze webshop ze toont: zonder de verborgen producten, en in de volgorde van deze webshop
 * (ontwerp.producten.volgorde[categorie]; wat daar niet in staat komt erachter, in de volgorde van het dashboard). Zonder ontwerp
 * precies wat erin ging.
 */
export function metOntwerpProducten(lijst, ontwerp, idCategory) {
    if (!Array.isArray(lijst) || !ontwerp?.producten) return lijst;
    const weg = ontwerp.producten.verborgen || [];
    const volgorde = ontwerp.producten.volgorde?.[String(idCategory)] || [];
    if (weg.length === 0 && volgorde.length === 0) return lijst;
    const zichtbaar = lijst.filter((p) => !weg.includes(Number(p?.idproduct)));
    if (volgorde.length === 0) return zichtbaar.length === lijst.length ? lijst : zichtbaar;
    const plek = (p) => {
        const i = volgorde.indexOf(Number(p?.idproduct));
        return i < 0 ? volgorde.length + zichtbaar.indexOf(p) : i;
    };
    return [...zichtbaar].sort((a, b) => plek(a) - plek(b));
}

/** De uitgelichte producten van deze webshop (de ids, in volgorde), zonder de verborgen. Leeg zonder ontwerp. */
export function uitgelichtIds(ontwerp) {
    const weg = ontwerp?.producten?.verborgen || [];
    return (ontwerp?.producten?.uitgelicht || []).filter((id) => !weg.includes(id));
}

// ─── de bewerkstand in de editor ────────────────────────────────────────────────────────────────────────────────

/** De dingen in de webshop die je in de editor kunt aanklikken, met hun data-attribuut. */
export const BEWERKDOELEN = Object.freeze({
    tegel: "data-ws-tegel",
    tekst: "data-ws-tekst",
    categorie: "data-ws-categorie",
    product: "data-ws-product",
    menu: "data-ws-menu",
});

const KIEZER = Object.values(BEWERKDOELEN).map((a) => `[${a}]`).join(",");

/** Het dichtstbijzijnde aanklikbare ding vanaf dit element: { doel, waarde, element }, of null. */
export function bewerkdoel(element) {
    const el = element && typeof element.closest === "function" ? element.closest(KIEZER) : null;
    if (!el) return null;
    for (const [doel, attribuut] of Object.entries(BEWERKDOELEN)) {
        if (el.hasAttribute(attribuut)) return { doel, waarde: el.getAttribute(attribuut) || "", element: el };
    }
    return null;
}

/** Staat de widget als bewerkstand in de webshop-editor (?wseditor=1, in een iframe)? */
export function isEditor(zoek, binnen = typeof window !== "undefined" && window.parent !== window) {
    try {
        return binnen && new URLSearchParams(zoek || "").get("wseditor") === "1";
    } catch {
        return false;
    }
}

/** Moet de widget het concept laden (editor, of "Test je webshop" met concept=1)? */
export function wilConcept(zoek) {
    try {
        const p = new URLSearchParams(zoek || "");
        return p.get("wseditor") === "1" || p.get("concept") === "1";
    } catch {
        return false;
    }
}

/** Is dit een bericht van de editor, van de pagina eromheen (en van de verwachte herkomst als die bekend is)? */
export function isEditorbericht(gebeurtenis, ouder, verwacht) {
    const data = gebeurtenis?.data;
    if (!data || typeof data !== "object" || data.bron !== "jimani-dashboard") return false;
    if (!ouder || gebeurtenis.source !== ouder) return false;
    if (verwacht && verwacht !== "*" && gebeurtenis.origin !== verwacht) return false;
    return typeof data.soort === "string" && data.soort.startsWith("webshopontwerp");
}
